import React, { useState } from "react";
import { transformToHtml, getCrId } from "../../../utils/CommonUtils";
import {
  buildFlightStatusPwaDetailUrlWithDataParam,
  buildFlightStatusStreamPayload,
  getFlightStatusCurrencyClient,
  getFlightStatusLocaleClient,
} from "../../../utils/FlightStatusUtils";
import "react-day-picker/lib/style.css";
import DatePickerWrapper from "./DatePickerWrapper";

const PWA_FIELDS = {
  AIRLINE: { label: "Flight number", placeHolder: "E.G. 6E413" },
  DATE: { label: "Date", placeHolder: "Select date" },
};

const DEFAULT_CTA = { text: "SEARCH" };
const DEFAULT_META = { maxSelectablePastDays: 7, maxSelectableFutureDays: 30 };

function formatDateParts(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    const day = d.getDate();
    const month = d.toLocaleDateString("en-IN", { month: "short" });
    const weekday = d.toLocaleDateString("en-IN", { weekday: "short" });
    const year = d.getFullYear();
    return {
      bold: `${day} ${month},`,
      rest: ` ${weekday} ${year}`,
    };
  } catch {
    return null;
  }
}

function getSelectedDate(value) {
  if (!value) return new Date();
  try {
    return new Date(value);
  } catch {
    return new Date();
  }
}

function PlaneIcon() {
  return (
    <span className="fs-pwa-header__icon" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"
          stroke="#888888"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </span>
  );
}

function CalendarIcon() {
  return (
    <span className="fs-pwa-header__icon" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="5" width="18" height="16" rx="2" stroke="#888888" strokeWidth="1.35" fill="none" />
        <path d="M3 10h18M8 3v4M16 3v4" stroke="#888888" strokeWidth="1.35" strokeLinecap="round" />
      </svg>
    </span>
  );
}

export default function FlightStatusHeaderPWA({
  meta = DEFAULT_META,
  cta = DEFAULT_CTA,
  onSearch,
  formData: controlledFormData,
  setFormData: controlledSetFormData,
}) {
  const [internalFormData, setInternalFormData] = useState({
    AIRLINE: "",
    DATE: new Date().toISOString().slice(0, 10),
  });

  const formData = controlledFormData ?? internalFormData;
  const setFormData = controlledSetFormData ?? setInternalFormData;

  const [showDatepicker, setShowDatepicker] = useState(false);

  const handleFlightNumberChange = (e) => {
    setFormData({ ...formData, AIRLINE: e.target.value.toUpperCase() });
  };

  const handleDateChange = (date, modifiers) => {
    if (modifiers?.disabled) return;
    if (date) {
      setFormData({ ...formData, DATE: date.toISOString().slice(0, 10) });
    }
    setShowDatepicker(false);
  };

  const isOutsideAllowedRange = (day) => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysAgo = new Date(today);
      daysAgo.setDate(today.getDate() - (meta?.maxSelectablePastDays ?? 7));
      const daysFromNow = new Date(today);
      daysFromNow.setDate(today.getDate() + (meta?.maxSelectableFutureDays ?? 30));
      return day < daysAgo || day > daysFromNow;
    } catch {
      return false;
    }
  };

  const enableSearch = () => !!(formData.AIRLINE?.trim() && formData.DATE);

  const redirectToFlightStatusDetail = () => {
    const flightCode = (formData.AIRLINE || "").replace(/\s+/g, "");
    const dateParam = formData.DATE || new Date().toISOString().slice(0, 10);
    const payload = buildFlightStatusStreamPayload({
      flightCode,
      date: dateParam,
      crId: getCrId(),
      currency: getFlightStatusCurrencyClient(),
      locale: getFlightStatusLocaleClient(),
    });
    const detailUrl = buildFlightStatusPwaDetailUrlWithDataParam(payload);
    if (typeof window !== "undefined") {
      window.location.href = detailUrl;
    }
  };

  const handleSearch = () => {
    if (onSearch) onSearch(true);
    else redirectToFlightStatusDetail();
  };

  const dateParts = formatDateParts(formData.DATE);
  const dateLabel = dateParts ? (
    <>
      <span className="fs-pwa-header__date-bold">{dateParts.bold}</span>
      <span className="fs-pwa-header__date-rest">{dateParts.rest}</span>
    </>
  ) : (
    <span className="fs-pwa-header__date-placeholder">{PWA_FIELDS.DATE.placeHolder}</span>
  );

  return (
    <div className="fs-pwa-header">
      <div className="fs-pwa-header__card">
        <div className="fs-pwa-header__row">
          <PlaneIcon />
          <div className="fs-pwa-header__field-block">
            <div className="fs-pwa-header__field-label">{PWA_FIELDS.AIRLINE.label}</div>
            <input
              className="fs-pwa-header__input"
              value={formData.AIRLINE || ""}
              placeholder={PWA_FIELDS.AIRLINE.placeHolder}
              onChange={handleFlightNumberChange}
              aria-label={PWA_FIELDS.AIRLINE.label}
              maxLength={10}
              type="text"
              pattern="[A-Za-z0-9]*"
              title="Please enter a valid flight number"
            />
          </div>
        </div>
        <div className="fs-pwa-header__divider" />
        <div className="fs-pwa-header__row">
          <CalendarIcon />
          <div className="fs-pwa-header__field-block">
            <div className="fs-pwa-header__field-label">{PWA_FIELDS.DATE.label}</div>
            <button
              type="button"
              className="fs-pwa-header__date-trigger"
              onClick={() => setShowDatepicker((prev) => !prev)}
              aria-label={PWA_FIELDS.DATE.label}
            >
              {dateLabel}
            </button>
          </div>
        </div>
      </div>
      {cta && (
        <button
          type="button"
          className="fs-pwa-header__search"
          onClick={handleSearch}
          disabled={!enableSearch()}
        >
          {transformToHtml(cta.text)}
        </button>
      )}
      {showDatepicker && (
        <>
          <div className="fs-overlayBg" onClick={() => setShowDatepicker(false)} role="presentation" />
          <div className="datePickerContainer">
            <div className="dayPickerFlightWrap depart-cal">
              <DatePickerWrapper
                selectedDays={getSelectedDate(formData.DATE)}
                disabledDays={isOutsideAllowedRange}
                onDayClick={(day, modifiers) => {
                  if (day && !modifiers?.disabled) {
                    handleDateChange(day, modifiers);
                  }
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
